import React, { useState, useMemo, useEffect } from "react";
import styles from "./ReportCard.module.css";
import ReportSearchDropdown from "../ReportSearchDropdown/ReportSearchDropdown";
import AddButton from "../../../shared/AddButton/AddButton";
import ReportButton from "../ReportButton/ReportButton";
import { DatePicker } from "../../../shared/@forms/DateTimePicker/DatePicker/DatePicker";
import PropTypes from "prop-types";

const AVAILABLE_REPORTS = [
  "All Attendees",
  "Breakout Sessions",
  "ADA",
  "Qualified Group Attendance",
  "Qualified Group Overview",
  "Qualified Attendance",
  "Session Attendance",
  "Session Overview",
  "Questionnaire",
  "Platinum Plus",
  "Pacing Report",
  "Pacing Report with Diamond",
];

const ALWAYS_AVAILABLE_REPORTS = [
  "Ticket Purchases",
  "Ticket Purchases By Diamond",
];

const OWNER_FILTERED_REPORTS = [
  "ADA",
  "All Attendees",
  "Qualified Group Attendance",
  "Qualified Group Overview",
];

const dedupeById = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = String(item.id ?? item.value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const filterByEvents = (items, selectedEventIds) => {
  if (selectedEventIds.length === 0) return [];
  if (selectedEventIds.includes("-1")) return dedupeById(items);
  return dedupeById(
    items.filter((item) => selectedEventIds.includes(String(item.eventId)))
  );
};

const parseDate = (date) => {
  if (!date) return null;
  if (Array.isArray(date)) return date;

  if (date instanceof Date) {
    return [date.getFullYear(), date.getMonth() + 1, date.getDate()];
  }

  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split("-").map(Number);
    return [year, month, day];
  }

  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime())
    ? null
    : [
        parsedDate.getFullYear(),
        parsedDate.getMonth() + 1,
        parsedDate.getDate(),
      ];
};

const formatDateForRequest = (date) => {
  const dateValue = parseDate(date);
  if (!dateValue) return null;

  const [year, monthValue, dayValue] = dateValue;
  const month = String(monthValue).padStart(2, "0");
  const day = String(dayValue).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const ReportCard = ({
  filterData,
  onGenerateReport,
  onGeneratePDF,
  onSaveReport,
  onEdit,
  reportsLength,
  selectedYear,
  setSelectedYear,
  initialFilters,
}) => {
  const [year, setYear] = useState(
    selectedYear.toString() || initialFilters?.year
  );
  useEffect(() => {
    if (year) setSelectedYear(year);
  }, [year, setSelectedYear]);
  const [selectedEventType, setSelectedEventType] = useState(
    initialFilters?.type || null
  );
  const [selectedReport, setSelectedReport] = useState(
    initialFilters?.report || null
  );

  const [eventTypeOptions, setEventTypeOptions] = useState([]);

  const validFilter = (val) => {
    if (val === null || val === undefined) return false;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === "string") return val.trim() !== "";
    return true;
  };

  const [events, setEvents] = useState(
    validFilter(initialFilters?.rawEvents)
      ? initialFilters.rawEvents
      : [{ id: null, value: null }]
  );
  const [owners, setOwners] = useState(
    validFilter(initialFilters?.rawOwners)
      ? initialFilters.rawOwners
      : [{ id: null, value: null }]
  );
  const [groups, setGroups] = useState(
    validFilter(initialFilters?.rawGroups)
      ? initialFilters.rawGroups
      : [{ id: null, value: null }]
  );
  const [sessions, setSessions] = useState(
    validFilter(initialFilters?.rawSessions)
      ? initialFilters.rawSessions
      : [{ id: null, value: null }]
  );
  const [eventSets, setEventSets] = useState([{ id: null, value: "All" }]);
  const [asOfDate, setAsOfDate] = useState(parseDate(initialFilters?.asOfDate));

  useEffect(() => {
    setEventTypeOptions(
      Array.isArray(filterData) && filterData.length
        ? filterData.map((f) => f.eventType)
        : []
    );
  }, [filterData]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const yearsArr = [];
    for (let y = 2025; y <= currentYear + 1; y++) yearsArr.push(y.toString());
    return yearsArr;
  }, []);

  const handleEdit = () => {
    if (onEdit) onEdit();
  };

  const addItem = (setState) => {
    setState((prev) => [...prev, { id: null, value: null }]);
    handleEdit();
  };

  const removeItem = (id, setState) => {
    setState((prev) => prev.filter((item) => item.id !== id));
    handleEdit();
  };

  const selectedFilterData = filterData?.find(
    (f) => f.eventType === selectedEventType
  );

  const availableReports = useMemo(() => {
    if (!selectedFilterData) return ALWAYS_AVAILABLE_REPORTS;

    const backendReports = Array.isArray(selectedFilterData.reports)
      ? selectedFilterData.reports.map((r) =>
          r === "Questionaire" || r === "Questionnaire" ? "Questionnaire" : r
        )
      : [];

    const filteredBackendReports = backendReports.filter((r) =>
      AVAILABLE_REPORTS.includes(r)
    );

    if (selectedFilterData.eventType === "Family Reunions") {
      return Array.from(new Set(filteredBackendReports)).sort((a, b) =>
        a.localeCompare(b)
      );
    }

    return Array.from(
      new Set([...ALWAYS_AVAILABLE_REPORTS, ...filteredBackendReports])
    ).sort((a, b) => a.localeCompare(b));
  }, [selectedFilterData]);

  const hasEventOwnerOptions =
    Array.isArray(selectedFilterData?.filters?.eventOwner) &&
    dedupeById(selectedFilterData.filters.eventOwner).length > 1;

  const showOwners =
    selectedEventType &&
    selectedReport &&
    hasEventOwnerOptions &&
    (selectedReport === "ADA" ||
      selectedReport === "All Attendees" ||
      selectedReport === "Qualified Group Attendance" ||
      selectedReport === "Qualified Group Overview" ||
      ([
        "Ticket Purchases",
        "Ticket Purchases By Diamond",
        "Qualified Attendance",
      ].includes(selectedReport) &&
        (selectedEventType.toLowerCase().includes("family reunion") ||
          selectedEventType.toLowerCase().includes("summer conference"))));

  const requireEvent =
    selectedReport === "ADA" ||
    selectedReport === "All Attendees" ||
    selectedReport === "Session Overview" ||
    selectedReport === "Session Attendance" ||
    selectedReport === "Questionnaire" ||
    selectedReport === "Qualified Group Overview" ||
    selectedReport === "Breakout Sessions" ||
    selectedReport === "Platinum Plus" ||
    (selectedReport === "Qualified Group Attendance" &&
      (selectedEventType.toLowerCase().includes("summer conference") ||
        selectedEventType.toLowerCase().includes("regional") ||
        selectedEventType.toLowerCase().includes("second look meeting")));

  const showGroups =
    selectedEventType &&
    selectedReport &&
    selectedReport === "Qualified Group Attendance";

  const showEvents =
    selectedEventType &&
    selectedReport &&
    (selectedReport === "Qualified Group Attendance" ||
      selectedReport === "Qualified Group Overview" ||
      selectedReport === "ADA" ||
      selectedReport === "Breakout Sessions" ||
      selectedReport === "All Attendees" ||
      selectedReport === "Session Attendance" ||
      selectedReport === "Session Overview" ||
      selectedReport === "Questionnaire" ||
      selectedReport === "Platinum Plus" ||
      selectedReport === "Pacing Report" ||
      selectedReport === "Pacing Report with Diamond");

  const showAsOfDate =
    selectedEventType &&
    selectedReport &&
    (selectedReport === "Pacing Report" ||
      selectedReport === "Pacing Report with Diamond");

  const showSessions =
    selectedEventType &&
    selectedReport &&
    selectedReport === "Session Attendance";

  const showEventSets =
    selectedEventType && selectedReport === "Breakout Sessions";

  const isGenerateDisabled = () => {
    if (!selectedReport) return true;
    if (!selectedEventType) return true;
    const hasUnselected = (arr, includeAllForFirst = false) =>
      Array.isArray(arr) &&
      arr.some((i, idx) => {
        if (includeAllForFirst && idx === 0 && !i.value) return false;
        return !i.value || i.value === "Select";
      });
    if (showOwners && hasUnselected(owners, true)) return true;
    if (
      showEvents &&
      selectedReport !== "Platinum Plus" &&
      hasUnselected(events, !requireEvent)
    )
      return true;
    if (showSessions && hasUnselected(sessions, false)) return true;
    if (showGroups && hasUnselected(groups, false)) return true;
    return false;
  };

  useEffect(() => {
    if (!selectedReport) return;

    if (!showOwners) setOwners([{ id: null, value: null }]);
    if (!showEvents) setEvents([{ id: null, value: null }]);
    if (!showGroups) setGroups([{ id: null, value: null }]);
    if (!showSessions) setSessions([{ id: null, value: null }]);
    if (!showEventSets) setEventSets([{ id: null, value: "All" }]);
    if (!showAsOfDate) setAsOfDate(null);
  }, [
    selectedReport,
    showOwners,
    showEvents,
    showGroups,
    showSessions,
    showEventSets,
    showAsOfDate,
  ]);

  useEffect(() => {
    setSelectedEventType(null);
    setSelectedReport(null);
    setEvents([{ id: null, value: null }]);
    setOwners([{ id: null, value: null }]);
    setGroups([{ id: null, value: null }]);
    setSessions([{ id: null, value: null }]);
    setEventSets([{ id: null, value: "All" }]);
    setAsOfDate(null);
  }, [year]);

  const selectedEventIds = events
    .filter((e) => e.id && e.value !== "All")
    .map((e) => String(e.id));

  const hasSelectedEvent = selectedEventIds.length > 0;

  const filteredGroups = useMemo(
    () =>
      filterByEvents(
        selectedFilterData?.filters?.groups || [],
        selectedEventIds
      ),
    [selectedFilterData, selectedEventIds]
  );

  const filteredSessions = useMemo(
    () =>
      filterByEvents(
        selectedFilterData?.filters?.sessions || [],
        selectedEventIds
      ),
    [selectedFilterData, selectedEventIds]
  );

  const filteredEventSets = useMemo(
    () =>
      filterByEvents(
        selectedFilterData?.filters?.eventSets || [],
        selectedEventIds
      ),
    [selectedFilterData, selectedEventIds]
  );

  const filteredOwners = useMemo(
    () => dedupeById(selectedFilterData?.filters?.eventOwner || []),
    [selectedFilterData]
  );

  const filteredEventsByOwner = useMemo(() => {
    let allEvents = selectedFilterData?.filters?.events || [];

    // For Breakout Sessions, only show events that have at least one event set
    if (selectedReport === "Breakout Sessions") {
      const eventSetIds = new Set(
        (selectedFilterData?.filters?.eventSets || []).map((es) =>
          String(es.eventId)
        )
      );
      allEvents = allEvents.filter((event) =>
        eventSetIds.has(String(event.id))
      );
    }

    if (!OWNER_FILTERED_REPORTS.includes(selectedReport)) return allEvents;

    const selectedOwnerValues = owners
      .map((o) => o.value)
      .filter((v) => v && v !== "All");

    if (selectedOwnerValues.length === 0) return allEvents;

    return allEvents.filter((event) =>
      selectedOwnerValues.includes(event.eventOwner)
    );
  }, [selectedFilterData, owners, selectedReport]);

  useEffect(() => {
    if (!OWNER_FILTERED_REPORTS.includes(selectedReport)) return;
    setEvents([{ id: null, value: null }]);
    setGroups([{ id: null, value: null }]);
  }, [owners, selectedReport]);

  const renderDropdowns = (
    items,
    setItems,
    allOptions,
    label,
    includeAll = false,
    disabled = false
  ) => {
    return items.map((item, idx) => {
      const selectedValues = items
        .map((i, iIdx) => {
          if (!i.value && includeAll && iIdx === 0) return "All";
          return i.value;
        })
        .filter(Boolean);

      const options = allOptions.filter(
        (opt) => !selectedValues.includes(opt.value) || opt.value === item.value
      );

      if (includeAll) {
        if (!selectedValues.includes("All") || item.value === "All") {
          options.unshift({ id: null, value: "All" });
        }
      }

      const isLast = idx === items.length - 1;
      const canRemove = items.length > 1;
      const canAdd =
        items.length < allOptions.length &&
        selectedReport !== "Session Overview" &&
        !(label === "Event" && selectedReport === "Pacing Report");

      return (
        <div key={idx} className={styles.dropdownGroupAligned}>
          <ReportSearchDropdown
            label={idx === 0 ? label : ""}
            options={options.map((o) => o.value)}
            defaultValue={
              item.value || (idx === 0 && includeAll ? "All" : "Select")
            }
            isFilter
            disabled={disabled}
            onSelect={(val) => {
              const selectedOption = options.find((o) => o.value === val);

              setItems((prev) =>
                prev.map((i, iIdx) =>
                  iIdx === idx
                    ? {
                        id:
                          selectedOption?.id != null
                            ? parseInt(selectedOption.id, 10)
                            : null,
                        value: val,
                      }
                    : i
                )
              );

              if (label === "Event") {
                setGroups([{ id: null, value: null }]);
                setSessions([{ id: null, value: null }]);
                setEventSets([{ id: null, value: "All" }]);
              }

              handleEdit();
            }}
          />
          <div className={styles.btnGroup}>
            {canRemove && (
              <AddButton
                isPlus={false}
                onClick={() => removeItem(item.id, setItems)}
              />
            )}
            {isLast &&
              canAdd &&
              (label === "Event" &&
              selectedReport !== "Session Overview" &&
              selectedEventType === "Qualified Group Attendance"
                ? items.length < 2
                : true) && (
                <AddButton isPlus onClick={() => addItem(setItems)} />
              )}
          </div>
        </div>
      );
    });
  };

  return (
    <div className={styles.card}>
      <div className={styles.mainContent}>
        <div className={styles.inputSection}>
          <h3 className={styles.title}>Event Reporting</h3>

          <div className={styles.dropdownGroupAligned}>
            <div className={styles.dropdownTopRow}>
              <ReportSearchDropdown
                label="Year"
                options={yearOptions}
                defaultValue={year || "Select"}
                isYear
                onSelect={setYear}
              />
            </div>

            <div className={styles.dropdownTopRow}>
              <ReportSearchDropdown
                label="Event Type"
                options={eventTypeOptions}
                defaultValue={selectedEventType || "Select"}
                onSelect={(val) => {
                  setSelectedEventType(val);
                  setSelectedReport(null);
                  setEvents([{ id: null, value: null }]);
                  setOwners([{ id: null, value: null }]);
                  setGroups([{ id: null, value: null }]);
                  setSessions([{ id: null, value: null }]);
                  setEventSets([{ id: null, value: "All" }]);
                  setAsOfDate(null);
                }}
              />
            </div>
          </div>
          {selectedEventType && <hr className={styles.separator} />}
          <div className={styles.reportRow}>
            {selectedEventType && (
              <div className={styles.reportItem}>
                <ReportSearchDropdown
                  label="Report"
                  options={availableReports}
                  defaultValue={selectedReport || "Select"}
                  onSelect={(val) => setSelectedReport(val)}
                />
              </div>
            )}
          </div>

          {selectedEventType && selectedReport && selectedFilterData && (
            <>
              {(showOwners || showGroups || showEvents || showAsOfDate) && (
                <hr className={styles.separator} />
              )}
              {showOwners && (
                <div className={styles.dropdownRow}>
                  {renderDropdowns(
                    owners,
                    setOwners,
                    filteredOwners,
                    "Event Owner",
                    true
                  )}
                </div>
              )}
              {showEvents && (
                <div className={styles.dropdownRow}>
                  {renderDropdowns(
                    events,
                    setEvents,
                    filteredEventsByOwner,
                    "Event"
                  )}
                </div>
              )}
              {showSessions && (
                <div className={styles.dropdownRow}>
                  {renderDropdowns(
                    sessions,
                    setSessions,
                    filteredSessions,
                    "Session",
                    false,
                    !hasSelectedEvent
                  )}
                </div>
              )}
              {showGroups && (
                <div className={styles.dropdownRow}>
                  {renderDropdowns(
                    groups,
                    setGroups,
                    filteredGroups,
                    "Group",
                    false,
                    !hasSelectedEvent
                  )}
                </div>
              )}
              {showEventSets && (
                <div className={styles.dropdownRow}>
                  <div className={styles.dropdownGroupAligned}>
                    <ReportSearchDropdown
                      label="Event Set"
                      options={[
                        "All",
                        ...filteredEventSets.map((es) => es.value),
                      ]}
                      defaultValue={eventSets[0]?.value}
                      isFilter
                      disabled={!hasSelectedEvent}
                      onSelect={(val) => {
                        const selectedOption = filteredEventSets.find(
                          (es) => es.value === val
                        );
                        setEventSets([
                          { id: selectedOption?.id ?? null, value: val },
                        ]);
                        handleEdit();
                      }}
                    />
                  </div>
                </div>
              )}
              {showAsOfDate && (
                <div className={styles.dropdownRow}>
                  <div className={styles.dateFilterContainer}>
                    <label className={styles.dateFilterLabel}>As Of Date</label>
                    <div className={styles.dateFilterWrapper}>
                      <DatePicker
                        value={asOfDate}
                        onChange={(date) => {
                          setAsOfDate(date);
                          handleEdit();
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.buttonDivider}></div>
        <div className={styles.buttonSection}>
          <ReportButton
            label="Generate Report"
            onClick={() => {
              onGenerateReport &&
                onGenerateReport({
                  year,
                  events: events.map((e) =>
                    e.value === "All" || e.id == null
                      ? null
                      : parseInt(e.id, 10)
                  ),
                  sessions: sessions.map((e) =>
                    e.value === "All" || e.id == null
                      ? null
                      : parseInt(e.id, 10)
                  ),
                  groups: groups.map((g) =>
                    g.value === "All" || g.id == null
                      ? null
                      : parseInt(g.id, 10)
                  ),
                  owners: owners.map((o) =>
                    o.value === "All" ? null : o.value
                  ),
                  type: selectedEventType,
                  report: selectedReport,
                  eventSetId:
                    !eventSets[0]?.value || eventSets[0]?.value === "All"
                      ? null
                      : eventSets[0]?.id,
                  asOfDate: showAsOfDate
                    ? formatDateForRequest(asOfDate)
                    : null,
                  eventNames: events.map((e) =>
                    e.value === "All" ? null : e.value
                  ),
                  groupNames: groups.map((g) =>
                    g.value === "All" ? null : g.value
                  ),
                  rawEvents: events,
                  rawGroups: groups,
                  rawOwners: owners,
                  rawSessions: sessions,
                  rawEventSets: eventSets,
                });
            }}
            disabled={isGenerateDisabled()}
          />
          <ReportButton
            label="Generate PDF"
            onClick={() => onGeneratePDF && onGeneratePDF()}
            disabled={reportsLength === 0}
          />
          <ReportButton
            label="Save Report"
            onClick={() => onSaveReport && onSaveReport(selectedReport)}
            disabled={reportsLength === 0}
          />
        </div>
      </div>
    </div>
  );
};

ReportCard.propTypes = {
  onGenerateReport: PropTypes.func,
  onGeneratePDF: PropTypes.func,
  onSaveReport: PropTypes.func,
  onEdit: PropTypes.func,
};

export default ReportCard;
